// Em renderInputItem.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Form, FormInstance } from 'components/ds';
import { ValidatorField } from '../../../../components/ValidatorField';
import { CustomInput } from '../DynamicForm/DynamicForm.styles';
import { FormItemProps } from '../../_types/dynamicFormInterfaces.types';
import { useAccountVerification } from '../../hooks/useAccountVerification';
import { IdentifierType } from '../../_types/account.types';
import { ExistingIdentifiers } from '../DynamicForm/DynamicForm';
import { useExistingIdentifiers } from '../../context/ExistingIdentifiersContext';

interface InputItemProps {
  item: FormItemProps;
  isDisabled: boolean;
  loading?: boolean;
  form?: FormInstance;
}

export const InputItem: React.FC<InputItemProps> = ({
                                                      item,
                                                      isDisabled,
                                                      loading,
                                                      form
                                                    }) => {
  const maxLengthRule = item.rules.find(rule => rule.max !== undefined);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const {verifyAccount, loading: verifyLoading} = useAccountVerification();
  const lastValidValueRef = useRef<string | null>(null);
  const {existingIdentifiers, setExistingIdentifiers, setIsVerifying} = useExistingIdentifiers();
  
  if (maxLengthRule) {
    item.maxLength = maxLengthRule.max;
  }
  
  // Função para converter data ISO para formato YYYY-MM-DD
  const formatDateForInput = (dateValue: string): string => {
    if (!dateValue) return '';
    
    try {
      // Se já está no formato YYYY-MM-DD, retorna como está
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      
      // Se é uma data ISO, converte para YYYY-MM-DD
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return '';
    }
  };
  
  const cleanPhoneNumber = (phone: string): string => phone.replace(/\D/g, '');
  
  const handleVerification = async (identifierType: IdentifierType, value: string) => {
    try {
      const cleanedValue = identifierType === IdentifierType.PHONE_NUMBER ? cleanPhoneNumber(value) : value;
      
      const response = await verifyAccount(identifierType, cleanedValue);
      
      const fieldName = identifierType === IdentifierType.EMAIL ? 'email' : 'phoneNumber';
      const exists = response && response.exists;
      
      setExistingIdentifiers(prev => {
        const newState = {
          ...prev,
          [fieldName]: exists
        };
        setIsVerifying(false)
        
        return newState;
      });
      
      if (exists && form) {
        
        try {
          await form.validateFields([item.name]);
        } catch (error) {
          console.error('Erro na validação:', error);
        }
        
        const currentValue = form.getFieldValue(item.name);
        form.setFieldsValue({[item.name]: ''});
        setTimeout(() => {
          form.setFieldsValue({[item.name]: currentValue});
          form.validateFields([item.name]).catch(e => {
          });
        }, 0);
      }
    } catch (error) {
      console.error('Erro ao verificar conta:', error);
    }
  };
  
  const validateRules = async (value: any): Promise<boolean> => {
    try {
      for (const rule of item.rules) {
        try {
          await ValidatorField.validator(rule, value, item);
        } catch (error) {
          return false;
        }
      }
      
      return true;
    } catch (e) {
      return false;
    }
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target;
    
    if (item.name === 'email' || item.name === 'phoneNumber') {
      setIsVerifying(true)
      
      setExistingIdentifiers((prev: ExistingIdentifiers) => ({
        ...prev,
        [item.name]: false
      }));
      
      if (timer) {
        clearTimeout(timer);
        setTimer(null);
      }
      
      validateRules(value).then(isValid => {
        if (isValid) {
          lastValidValueRef.current = value;
          
          const newTimer = setTimeout(() => {
            if (item.name === 'email') {
              handleVerification(IdentifierType.EMAIL, value);
            } else if (item.name === 'phoneNumber') {
              handleVerification(IdentifierType.PHONE_NUMBER, value);
            }
          }, 1000);
          
          setTimer(newTimer);
        } else {
          lastValidValueRef.current = null;
        }
      });
    }
  };
  
  useEffect(() => () => {
    if (timer) {
      clearTimeout(timer);
    }
  }, [timer]);
  
  useEffect(() => {
    if ((item.name === 'email' && existingIdentifiers.email) ||
      (item.name === 'phoneNumber' && existingIdentifiers.phoneNumber)) {
      if (form) {
        form.validateFields([item.name])
      }
    }
  }, [existingIdentifiers, item.name, form]);
  
  // Log específico para campos de data
  useEffect(() => {
    if (item.type === 'date' && item.initialValue) {
      console.log(`[InputItem] Campo de data ${item.name}:`, {
        initialValue: item.initialValue,
        type: item.type,
        isDisabled
      });
      
      // Para campos de data, garantir que o valor seja aplicado após a renderização
      if (form) {
        setTimeout(() => {
          const currentValue = form.getFieldValue(item.name);
          if (!currentValue && item.initialValue) {
            console.log(`[InputItem] Aplicando valor para campo de data ${item.name}:`, item.initialValue);
            form.setFieldsValue({
              [item.name]: item.initialValue
            });
          }
        }, 200);
      }
    }
  }, [item.initialValue, item.name, item.type, isDisabled, form]);
  
  // Log para debug dos valores
  useEffect(() => {
    console.log(`[InputItem] Campo ${item.name}:`, {
      initialValue: item.initialValue,
      type: item.type,
      isDisabled
    });
  }, [item.name, item.initialValue, item.type, isDisabled]);
  
  return (
    <Form.Item
      key={item.name}
      name={item.name}
      rules={[
        ...item.rules,
        {validator: (rule: any, value: any) => ValidatorField.validator(rule, value, item)},
        {
          validator: async (_: any, value: any) => {
            if (!value) return Promise.resolve();
            
            if (item.name === 'email' && existingIdentifiers.email) {
              return Promise.reject('Este e-mail já está cadastrado, por favor, utilize outro e-mail válido.');
            }
            
            if (item.name === 'phoneNumber' && existingIdentifiers.phoneNumber) {
              return Promise.reject('Este telefone já está cadastrado, por favor, informe outro número de celular válido.');
            }
            
            return Promise.resolve();
          }
        }
      ]}
      initialValue={item.initialValue}
      validateTrigger={['onChange', 'onBlur']}
    >
      <CustomInput
        type={item.type}
        mask={item.mask}
        maxLength={item.mask ? undefined : (item.maxLength)}
        disabled={isDisabled}
        placeholder={item.label || item.placeholder}
        size="large"
        loading={loading || verifyLoading}
        onChange={handleChange}
        name={item.name} // Adicionar o nome do campo para debug
        // Remover value prop para deixar o Form controlar
      />
    </Form.Item>
  );
};

export const renderInputItem = (
  item: FormItemProps,
  isDisabled: boolean,
  loading?: boolean,
  form?: FormInstance
) => (
  <InputItem
    key={item.name}
    item={item}
    isDisabled={isDisabled}
    loading={loading}
    form={form}
  />
);
